// Library methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { HubConnection } from "@microsoft/signalr";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import VFGraphs from "./VFGraphs/VFGraphs";
import NonVFGraphs from "./NonVFGraphs/NonVFGraphs";

// Contexts
import { useLiveUpdateContext } from "src/contexts/live-update/LiveUpdateContext";

type GraphsType = {
  connection: HubConnection | null;
};

const Graphs = ({ connection }: GraphsType) => {
  const { t } = useTranslation();

  const {
    isPaused,
    isConfirmation,
    inTutorial,
    inCalibration,
    examReportLive,
    isVisualField,
    isEsterman,
  } = useLiveUpdateContext();

  const showNoDataAvailable = useMemo(
    () => !examReportLive && (!isPaused || isConfirmation) && !inCalibration,
    [examReportLive, isPaused, isConfirmation, inCalibration]
  );

  const showPaused = useMemo(
    () => !examReportLive && isPaused,
    [examReportLive, isPaused]
  );

  const showHidingChart = useMemo(
    () => inTutorial || inCalibration,
    [inTutorial, inCalibration]
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      padding="8px"
      gap="8px"
      height="100%"
      sx={{
        border: "1px solid #5B6D7A40",
        borderRadius: "4px",
        // Contains overflow within this bordered card instead of letting
        // tall content (e.g. VFGraphs' min-height floors on a short
        // browser window) visually spill past the border -- the modal's
        // own scrollbar only bounds the whole dialog, not this card.
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      {showPaused || showNoDataAvailable || showHidingChart ? (
        // Show info / error text if the graph(s) can't be displayed
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          height="100%"
        >
          <Typography fontSize="14px">
            {showPaused
              ? t("Exam is currently paused.")
              : showNoDataAvailable
                ? t("No Data Available.")
                : showHidingChart
                  ? t("Exam data will appear once the exam starts.")
                  : ""}
          </Typography>
        </Box>
      ) : isVisualField || isEsterman ? (
        <VFGraphs />
      ) : (
        <NonVFGraphs connection={connection} />
      )}
    </Box>
  );
};

export default Graphs;
